/**
 * Kader AI Icon Library
 * ─────────────────────
 * All SVG icons extracted from mockups. Human-drawn, no AI imagery.
 */
import React from 'react';
import { colors } from '@/design-system/tokens';

type IconProps = {
  size?: number;
  color?: string;
};

// ─── Navigation ──────────────────────────────────────────────────────────────

export const BackIcon: React.FC<IconProps> = ({ size = 20, color = colors.textPrimary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

export const ChevronRight: React.FC<IconProps> = ({ size = 16, color = colors.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <path d="M9 18l6-6-6-6"/>
  </svg>
);

// ─── Morning Brief ───────────────────────────────────────────────────────────

export const SunIcon: React.FC<IconProps> = ({ size = 20, color = colors.accent }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
    <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
    <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
    <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
  </svg>
);

export const SunFilled: React.FC<IconProps> = ({ size = 20, color = colors.accent }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <circle cx="12" cy="12" r="4"/>
    <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round"/>
  </svg>
);

export const RunIcon: React.FC<IconProps> = ({ size = 22, color = colors.textPrimary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="13.5" cy="3.5" r="2"/>
    <path d="M7 21l3-7 2.5 2 4-6"/><path d="M16 10l-2.5 3L11 11l-4 5"/>
    <path d="M6 16l-2 5"/><path d="M17 10l3 0"/>
  </svg>
);

export const ShieldIcon: React.FC<IconProps> = ({ size = 18, color = colors.cyan }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
  </svg>
);

export const HeartIcon: React.FC<IconProps> = ({ size = 18, color = colors.hrRed }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <path d="M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 000-7.78z"/>
  </svg>
);

export const FireIcon: React.FC<IconProps> = ({ size = 14, color = colors.accent }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <path d="M12 23c-4.97 0-9-3.58-9-8 0-3.19 2.13-6.17 4-8l1 3c.45-.78 2-3.5 2-5C10 3 12 1 12 1s2 2 2 4c0 1.5 1.55 4.22 2 5l1-3c1.87 1.83 4 4.81 4 8 0 4.42-4.03 8-9 8z"/>
  </svg>
);

// ─── Food & Nutrition ────────────────────────────────────────────────────────

export const ForkKnifeIcon: React.FC<IconProps> = ({ size = 18, color = colors.textPrimary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 002-2V2"/><path d="M7 2v20"/><path d="M21 15V2v0a5 5 0 00-5 5v6c0 1.1.9 2 2 2h3zm0 0v7"/>
  </svg>
);

export const CameraIcon: React.FC<IconProps> = ({ size = 28, color = colors.textPrimary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/>
    <circle cx="12" cy="13" r="4"/>
  </svg>
);

// ─── Mental & Brain ──────────────────────────────────────────────────────────

export const BrainIcon: React.FC<IconProps> = ({ size = 18, color = colors.textPrimary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2a7 7 0 017 7c0 2.5-1.3 4.7-3.3 6L12 22l-3.7-7C6.3 13.7 5 11.5 5 9a7 7 0 017-7z"/>
    <circle cx="12" cy="9" r="2"/>
  </svg>
);

// ─── Calendar & Schedule ─────────────────────────────────────────────────────

export const CalendarIcon: React.FC<IconProps> = ({ size = 16, color = colors.textSecondary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
  </svg>
);

export const CalendarSession2: React.FC<IconProps> = ({ size = 18, color = colors.blue }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <rect x="2" y="7" width="20" height="15" rx="2"/><path d="M16 2v5"/><path d="M8 2v5"/><path d="M2 12h20"/>
  </svg>
);

// ─── Chat ────────────────────────────────────────────────────────────────────

export const ChatIcon: React.FC<IconProps> = ({ size = 20, color = colors.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
  </svg>
);

export const SendIcon: React.FC<IconProps> = ({ size = 18, color = colors.bgPrimary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="19" x2="12" y2="5"/><polyline points="5 12 12 5 19 12"/>
  </svg>
);

export const MicIcon: React.FC<IconProps> = ({ size = 18, color = colors.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <rect x="9" y="1" width="6" height="12" rx="3"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><line x1="12" y1="19" x2="12" y2="23"/><line x1="8" y1="23" x2="16" y2="23"/>
  </svg>
);

// ─── User & Profile ──────────────────────────────────────────────────────────

export const UserIcon: React.FC<IconProps> = ({ size = 20, color = colors.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/>
  </svg>
);

// ─── Status & Feedback ───────────────────────────────────────────────────────

export const CheckIcon: React.FC<IconProps> = ({ size = 16, color = colors.bgPrimary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

export const PlusIcon: React.FC<IconProps> = ({ size = 16, color = colors.textSecondary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
  </svg>
);

export const SearchIcon: React.FC<IconProps> = ({ size = 18, color = colors.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
  </svg>
);

export const ClockIcon: React.FC<IconProps> = ({ size = 14, color = colors.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
  </svg>
);

export const LockIcon: React.FC<IconProps> = ({ size = 12, color = colors.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round">
    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0110 0v4"/>
  </svg>
);

// ─── Outdoor / Treadmill ─────────────────────────────────────────────────────

export const OutdoorIcon: React.FC<IconProps> = ({ size = 14, color = colors.textSecondary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41"/>
    <circle cx="12" cy="12" r="4"/>
  </svg>
);

export const TreadmillIcon: React.FC<IconProps> = ({ size = 14, color = colors.textSecondary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <rect x="2" y="6" width="20" height="12" rx="2"/>
    <line x1="6" y1="18" x2="6" y2="22"/><line x1="18" y1="18" x2="18" y2="22"/>
    <line x1="2" y1="14" x2="22" y2="14"/>
  </svg>
);

// ─── Gamification ────────────────────────────────────────────────────────────

/** Per-milestone badge colorways (day → token color). 365 also gets a double ring. */
export const MILESTONE_COLORS: Record<number, string> = {
  3: colors.cyan,
  7: colors.green,
  14: colors.blue,
  30: colors.purple,
  50: colors.yellow,
  100: colors.accent,
  365: colors.accent,
};

/**
 * Streak milestone badge: shield outline + flame motif + day number.
 * One parameterized icon, seven colorways (see MILESTONE_COLORS) — text-in-SVG
 * precedent: KaderLogo. Day 365 renders a second inner ring.
 */
export const StreakBadgeIcon: React.FC<{ day: number; size?: number; color?: string }> = ({
  day, size = 44, color = colors.accent,
}) => (
  <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
    {/* Shield */}
    <path
      d="M24 45s16-8 16-20V10L24 4 8 10v15c0 12 16 20 16 20z"
      stroke={color} strokeWidth="2.5" strokeLinejoin="round" fill="none"
    />
    {day === 365 && (
      <path
        d="M24 41s12.5-6.5 12.5-16V12.4L24 7.6l-12.5 4.8V25c0 9.5 12.5 16 12.5 16z"
        stroke={color} strokeWidth="1" strokeLinejoin="round" fill="none" opacity="0.55"
      />
    )}
    {/* Flame */}
    <path
      d="M24 13.5c1.6 2 2.8 3.6 2.8 5.4 0 .8-.2 1.5-.5 2.1 1-.6 1.8-1.5 2.3-2.6 1.3 1.7 2.4 3.8 2.4 5.9 0 3.9-3.1 6.7-7 6.7s-7-2.8-7-6.7c0-2.1 1.1-4.2 2.4-5.9.5 1.1 1.3 2 2.3 2.6-.3-.6-.5-1.3-.5-2.1 0-1.8 1.2-3.4 2.8-5.4z"
      fill={color} opacity="0.35"
    />
    {/* Day number */}
    <text
      x="24" y="30" textAnchor="middle"
      fontSize={day >= 100 ? 10 : 12} fontWeight="700"
      fontFamily="'DM Mono', monospace" fill={color}
    >{day}</text>
  </svg>
);

// ─── Activity chart ──────────────────────────────────────────────────────────

export const ActivityIcon: React.FC<IconProps> = ({ size = 16, color = colors.textPrimary }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round">
    <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
  </svg>
);

// ─── Progress (bar chart) ───────────────────────────────────────────────────

export const ProgressIcon: React.FC<IconProps> = ({ size = 20, color = colors.textMuted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="12" width="4" height="9" rx="1"/>
    <rect x="10" y="7" width="4" height="14" rx="1"/>
    <rect x="17" y="3" width="4" height="18" rx="1"/>
  </svg>
);

// ─── Kader Logo ──────────────────────────────────────────────────────────────

export const KaderLogo: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <div style={{
    width: size, height: size, borderRadius: size * 0.25,
    background: colors.accent,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: size * 0.43, fontWeight: 700, color: colors.bgPrimary,
    fontFamily: "'DM Mono', monospace",
  }}>K</div>
);

// ─── Google Logo (login screen) ──────────────────────────────────────────────

export const GoogleLogo: React.FC<{ size?: number }> = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);
